// src/components/ContactPage.js
import React, { useState } from 'react';
import { PublicPage, PageHero } from './public/PublicLayout';

const labelClass = 'pub-text block text-sm font-medium mb-2';
const inputClass = 'pub-input w-full px-4 py-3 rounded-lg transition';

const NEXT_STEPS = [
    'A short call about how your fleet works.',
    'A live demo of LoadMemo.',
    'Pricing for your fleet size, with no obligation.',
    'A plan for moving your data and getting set up.',
];

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        fleetSize: '',
        phone: '',
        message: ''
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Handle form submission here (e.g., send to an API endpoint)
        console.log('Form submitted:', formData);
        alert("Request received. We'll contact you within 24 hours.");
        // Optionally reset form
        setFormData({
            name: '', email: '', company: '', fleetSize: '', phone: '', message: ''
        });
    };

    return (
        <PublicPage>
            <PageHero eyebrow="Contact sales" title="Book a demo" accent="of LoadMemo.">
                Fill out the form and we'll set up a demo for your fleet.
            </PageHero>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                <div className="grid lg:grid-cols-5 gap-8">

                    {/* Contact Form */}
                    <div className="pub-surface pub-line border lg:col-span-3 rounded-2xl shadow-lg shadow-black/20 p-6 sm:p-10">
                        <h2 className="pub-text text-2xl sm:text-3xl font-bold mb-8">
                            Request a demo
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="name" className={labelClass}>Full name <span className="pub-faint">*</span></label>
                                    <input type="text" id="name" name="name" required value={formData.name} onChange={handleInputChange} className={inputClass} placeholder="John Doe" />
                                </div>
                                <div>
                                    <label htmlFor="email" className={labelClass}>Email <span className="pub-faint">*</span></label>
                                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className={inputClass} placeholder="john@company.com" />
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="company" className={labelClass}>Company name</label>
                                    <input type="text" id="company" name="company" value={formData.company} onChange={handleInputChange} className={inputClass} placeholder="ABC Trucking" />
                                </div>
                                <div>
                                    <label htmlFor="fleetSize" className={labelClass}>Fleet size</label>
                                    <select id="fleetSize" name="fleetSize" value={formData.fleetSize} onChange={handleInputChange} className={inputClass}>
                                        <option value="">Select fleet size</option>
                                        <option value="1">Owner-operator (1 truck)</option>
                                        <option value="2-10">Small fleet (2-10 trucks)</option>
                                        <option value="11-30">Medium fleet (11-30 trucks)</option>
                                        <option value="31-50">Large fleet (31-50 trucks)</option>
                                        <option value="50+">Enterprise (50+ trucks)</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="phone" className={labelClass}>Phone</label>
                                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} className={inputClass} placeholder="(555) 123-4567" />
                            </div>

                            <div>
                                <label htmlFor="message" className={labelClass}>Message</label>
                                <textarea id="message" name="message" rows="4" value={formData.message} onChange={handleInputChange} className={`${inputClass} resize-none`} placeholder="What would you like LoadMemo to help with?"></textarea>
                            </div>

                            <p className="pub-faint text-sm"><span aria-hidden="true">*</span> Required</p>

                            <button type="submit" className="w-full py-3.5 pub-btn rounded-lg font-semibold text-lg transition">
                                <i className="fas fa-paper-plane mr-2" aria-hidden="true"></i>
                                Send request
                            </button>
                        </form>
                    </div>

                    {/* Contact Information */}
                    <div className="lg:col-span-2 space-y-8">
                        <div className="pub-surface pub-line border rounded-2xl shadow-lg shadow-black/20 p-6 sm:p-8">
                            <h3 className="pub-text text-xl sm:text-2xl font-bold mb-6">Other ways to reach us</h3>
                            <div className="space-y-6">
                                <div className="flex items-start">
                                    <span className="pub-soft w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <i className="fas fa-phone" aria-hidden="true"></i>
                                    </span>
                                    <div className="ml-4 min-w-0">
                                        <h4 className="pub-text font-semibold">Phone</h4>
                                        <p className="pub-muted">1-800-LOADMEMO</p>
                                        <p className="pub-faint text-sm">Mon-Fri 8AM-8PM EST</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <span className="pub-soft w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <i className="fas fa-envelope" aria-hidden="true"></i>
                                    </span>
                                    <div className="ml-4 min-w-0">
                                        <h4 className="pub-text font-semibold">Email</h4>
                                        <a href="mailto:info@loadmemo.com" className="pub-link break-all">info@loadmemo.com</a>
                                        <p className="pub-faint text-sm">Response within 2 business hours</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="pub-surface pub-line border rounded-2xl shadow-lg shadow-black/20 p-6 sm:p-8">
                            <h3 className="pub-text text-xl sm:text-2xl font-bold mb-6">What happens next</h3>
                            <ul className="pub-muted space-y-4">
                                {NEXT_STEPS.map((step) => (
                                    <li key={step} className="flex items-start">
                                        <i className="fas fa-circle-check pub-icon mr-3 mt-1" aria-hidden="true"></i>
                                        <span>{step}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </PublicPage>
    );
};

export default ContactPage;
